import React from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, Box, Typography } from '@mui/material';
import TextInput from '../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from './InspectionRadioGroup';
import {
  conditionOptions,
  conditionWithNaOptions,
  yesNoOptions,
  hvacAgeOptions,
  hvacSystemTypeOptions,
  splitSystemSubtypeOptions,
  SPLIT_SYSTEM_DUAL_SUBTYPES,
} from '../config/options';

export interface HvacSystemValues {
  id: string;
  location: string;
  manufacturer: string;
  age: string;
  ageCondition: string;
  systemType: string;
  splitSystemSubtype: string;
  indoorManufacturer: string;
  outdoorManufacturer: string;
  overallVisualCondition: string;
  overallOperation: string;
  properClearance: string;
  serviceRecordUpdated: string;
  notes: string;
  // Boiler-specific fields (only used when systemType is 'boiler')
  boilerAcPresent: string;
  boilerExpansionTankCondition: string;
  boilerHeatExchangerCondition: string;
  boilerPressureReliefCondition: string;
  boilerAutoFillCondition: string;
}

export function createDefaultHvacSystem(): HvacSystemValues {
  return {
    id: crypto.randomUUID(),
    location: '',
    manufacturer: '',
    age: '',
    ageCondition: '',
    systemType: '',
    splitSystemSubtype: '',
    indoorManufacturer: '',
    outdoorManufacturer: '',
    overallVisualCondition: '',
    overallOperation: '',
    properClearance: '',
    serviceRecordUpdated: '',
    notes: '',
    boilerAcPresent: '',
    boilerExpansionTankCondition: '',
    boilerHeatExchangerCondition: '',
    boilerPressureReliefCondition: '',
    boilerAutoFillCondition: '',
  };
}

/** @deprecated Use createDefaultHvacSystem() to get a fresh UUID each time. */
export const defaultHvacSystem: HvacSystemValues = createDefaultHvacSystem();

interface HvacSystemFieldsProps {
  values: HvacSystemValues;
  onChange: (field: keyof HvacSystemValues, value: any) => void;
  manufacturers: { value: string; label: string }[];
}

const subHeaderSx = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1.5,
};

const splitSectionSx = {
  bgcolor: '#f0f5fa',
  border: '1.5px solid #c8d8e8',
  borderRadius: 2,
  p: 2,
  mb: 2,
};

const getAgeColor = (value: string): string | undefined => {
  if (!value || value === 'n_a') return undefined;
  const map: Record<string, string> = { '1_10': '#4caf50', '11_15': '#ff9800', '15_plus': '#f44336' };
  return map[value];
};

const getAgeCondition = (value: string): string => {
  if (!value || value === 'n_a') return '';
  const map: Record<string, string> = { '1_10': 'good', '11_15': 'needs_attention', '15_plus': 'requires_service' };
  return map[value] || '';
};

const HvacSystemFields: React.FC<HvacSystemFieldsProps> = ({ values, onChange, manufacturers }) => {
  const isSplitSystem = values.systemType === 'split_system';
  const isDualSubtype = SPLIT_SYSTEM_DUAL_SUBTYPES.has(values.splitSystemSubtype);

  const handleSystemTypeChange = (val: string) => {
    onChange('systemType', val);
    if (val !== 'split_system') {
      onChange('splitSystemSubtype', '');
      onChange('indoorManufacturer', '');
      onChange('outdoorManufacturer', '');
    }
    if (val !== 'boiler') {
      onChange('boilerAcPresent', '');
      onChange('boilerExpansionTankCondition', '');
      onChange('boilerHeatExchangerCondition', '');
      onChange('boilerPressureReliefCondition', '');
      onChange('boilerAutoFillCondition', '');
    }
  };

  const handleSubtypeChange = (val: string) => {
    const wasD = SPLIT_SYSTEM_DUAL_SUBTYPES.has(values.splitSystemSubtype);
    const isNowD = SPLIT_SYSTEM_DUAL_SUBTYPES.has(val);
    onChange('splitSystemSubtype', val);
    if (wasD && !isNowD) {
      onChange('indoorManufacturer', '');
      onChange('outdoorManufacturer', '');
    }
    if (!wasD && isNowD) {
      onChange('manufacturer', '');
    }
  };

  const renderManufacturerSelect = (
    value: string,
    setValue: (nextValue: string) => void,
    options: { value: string; label: string }[],
  ) => (
    <FormControl fullWidth variant="filled">
      <InputLabel>Manufacturer</InputLabel>
      <Select
        value={value}
        onChange={(e) => {
          const label = e.target.value as string;
          setValue(label);
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.label}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="filled">
            <InputLabel>Age</InputLabel>
            <Select
              value={values.age}
              onChange={(e) => {
                const val = e.target.value as string;
                onChange('age', val);
                onChange('ageCondition', getAgeCondition(val));
                (e.target as HTMLInputElement).blur();
              }}
              sx={{
                ...(getAgeColor(values.age) && {
                  bgcolor: getAgeColor(values.age),
                  color: '#fff',
                  '&.Mui-focused': { bgcolor: getAgeColor(values.age) },
                  '& .MuiSelect-icon': { color: '#fff' },
                  '& .MuiFilledInput-input': { color: '#fff' },
                }),
              }}
            >
              {hvacAgeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextInput label="Location" value={values.location} valueChanged={(val) => onChange('location', val)} />
        </Grid>
      </Grid>

      <Typography sx={subHeaderSx}>Type</Typography>
      <Box sx={{ mb: 2 }}>
        <InspectionRadioGroup
          radioOptions={hvacSystemTypeOptions}
          value={values.systemType}
          setValue={handleSystemTypeChange}
        />
      </Box>

      {!isSplitSystem && (
        <Box sx={{ mb: 2 }}>
          {renderManufacturerSelect(values.manufacturer, (val) => onChange('manufacturer', val), manufacturers)}
        </Box>
      )}

      {isSplitSystem && (
        <Box sx={splitSectionSx}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#3d5a80',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1.5,
            }}
          >
            Split System Configuration
          </Typography>

          <Typography sx={subHeaderSx}>Sub-type</Typography>
          <Box sx={{ mb: 2 }}>
            <InspectionRadioGroup
              radioOptions={splitSystemSubtypeOptions}
              value={values.splitSystemSubtype}
              setValue={handleSubtypeChange}
            />
          </Box>

          {isDualSubtype ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#1565c0',
                    mb: 0.75,
                  }}
                >
                  Indoor Unit
                </Typography>
                {renderManufacturerSelect(
                  values.indoorManufacturer,
                  (val) => onChange('indoorManufacturer', val),
                  manufacturers,
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#e65100',
                    mb: 0.75,
                  }}
                >
                  Outdoor Unit
                </Typography>
                {renderManufacturerSelect(
                  values.outdoorManufacturer,
                  (val) => onChange('outdoorManufacturer', val),
                  manufacturers,
                )}
              </Grid>
            </Grid>
          ) : (
            values.splitSystemSubtype &&
            renderManufacturerSelect(values.manufacturer, (val) => onChange('manufacturer', val), manufacturers)
          )}
        </Box>
      )}

      <Typography sx={subHeaderSx}>Inspection Details</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {values.systemType === 'boiler' && (
          <>
            <Grid item xs={12}>
              <InspectionRadioGroup
                label="Air Conditioning"
                radioOptions={yesNoOptions}
                value={values.boilerAcPresent}
                setValue={(val) => onChange('boilerAcPresent', val)}
              />
            </Grid>
            <Grid item xs={12}>
              <InspectionRadioGroup
                label="Expansion Tank"
                radioOptions={conditionWithNaOptions}
                value={values.boilerExpansionTankCondition}
                setValue={(val) => onChange('boilerExpansionTankCondition', val)}
              />
            </Grid>
            <Grid item xs={12}>
              <InspectionRadioGroup
                label="Heat Exchanger"
                radioOptions={conditionWithNaOptions}
                value={values.boilerHeatExchangerCondition}
                setValue={(val) => onChange('boilerHeatExchangerCondition', val)}
              />
            </Grid>
            <Grid item xs={12}>
              <InspectionRadioGroup
                label="Pressure Relief"
                radioOptions={conditionWithNaOptions}
                value={values.boilerPressureReliefCondition}
                setValue={(val) => onChange('boilerPressureReliefCondition', val)}
              />
            </Grid>
            <Grid item xs={12}>
              <InspectionRadioGroup
                label="Auto-Fill"
                radioOptions={conditionWithNaOptions}
                value={values.boilerAutoFillCondition}
                setValue={(val) => onChange('boilerAutoFillCondition', val)}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Overall/Visual Condition"
            radioOptions={conditionOptions}
            value={values.overallVisualCondition}
            setValue={(val) => onChange('overallVisualCondition', val)}
          />
        </Grid>
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Overall Operation"
            radioOptions={conditionOptions}
            value={values.overallOperation}
            setValue={(val) => onChange('overallOperation', val)}
          />
        </Grid>
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Proper Clearance Around Equipment"
            radioOptions={yesNoOptions}
            value={values.properClearance}
            setValue={(val) => onChange('properClearance', val)}
          />
        </Grid>
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Service Record Updated"
            radioOptions={yesNoOptions}
            value={values.serviceRecordUpdated}
            setValue={(val) => onChange('serviceRecordUpdated', val)}
          />
        </Grid>
      </Grid>

      <TextInput
        label="HVAC Notes"
        value={values.notes}
        valueChanged={(val) => onChange('notes', val)}
        multiline
        rows={3}
      />
    </Box>
  );
};

export default HvacSystemFields;
